import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

axios.defaults.withCredentials = true;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SESSION_KEY = 'lemo_session';
const SESSION_TTL_MS = 60 * 60 * 1000; // 1 hour

const AuthContext = createContext(null);

// Persist session to localStorage (token + expiry)
const saveSession = (accessToken) => {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({
      t: accessToken,
      exp: Date.now() + SESSION_TTL_MS,
    }));
  } catch { /* storage full or blocked */ }
};

const loadSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.exp && Date.now() > data.exp) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return data.t || null;
  } catch { return null; }
};

const clearSession = () => {
  try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  try { localStorage.removeItem('token'); } catch { /* ignore */ }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(null);
  const isRefreshing = useRef(false);
  const failedQueue = useRef([]);

  const processQueue = (error, newToken) => {
    failedQueue.current.forEach(({ resolve, reject }) => {
      if (error) reject(error);
      else resolve(newToken);
    });
    failedQueue.current = [];
  };

  const updateToken = (newToken) => {
    tokenRef.current = newToken;
    setToken(newToken);
    if (newToken && newToken !== 'cookie') {
      saveSession(newToken);
    }
  };

  // Axios interceptors
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const t = tokenRef.current;
      if (t && t !== 'cookie') {
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });

    const resId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const req = error.config;
        if (
          !error.response || error.response.status !== 401 || req._retry ||
          req.url?.includes('/auth/login') || req.url?.includes('/auth/register') ||
          req.url?.includes('/auth/refresh') || req.url?.includes('/auth/me')
        ) {
          return Promise.reject(error);
        }

        if (isRefreshing.current) {
          return new Promise((resolve, reject) => {
            failedQueue.current.push({ resolve, reject });
          }).then((t) => {
            req.headers.Authorization = `Bearer ${t}`;
            req._retry = true;
            return axios(req);
          });
        }

        req._retry = true;
        isRefreshing.current = true;

        try {
          const res = await axios.post(`${API}/auth/refresh`);
          const newToken = res.data.access_token;
          if (newToken) {
            updateToken(newToken);
            setUser(res.data.user || user);
          }
          processQueue(null, newToken);
          req.headers.Authorization = `Bearer ${newToken}`;
          return axios(req);
        } catch (refreshErr) {
          processQueue(refreshErr, null);
          tokenRef.current = null;
          setToken(null);
          setUser(null);
          clearSession();
          return Promise.reject(refreshErr);
        } finally {
          isRefreshing.current = false;
        }
      }
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Init auth on page load
  useEffect(() => {
    const initAuth = async () => {
      // 1. Try saved session from localStorage (survives refresh + browser close for 1h)
      const savedToken = loadSession();
      if (savedToken) {
        tokenRef.current = savedToken;
        try {
          const res = await axios.get(`${API}/auth/me`);
          const userData = res.data.user || res.data;
          const freshToken = res.data.access_token || savedToken;
          updateToken(freshToken);
          setUser(userData);
          setLoading(false);
          return;
        } catch {
          // Saved token expired — try refresh below
        }
      }

      // 2. Try cookie-based auth
      try {
        const res = await axios.get(`${API}/auth/me`);
        const userData = res.data.user || res.data;
        const freshToken = res.data.access_token;
        updateToken(freshToken || 'cookie');
        setUser(userData);
        setLoading(false);
        return;
      } catch {
        // Cookie auth failed
      }

      // 3. Try refresh token
      try {
        const refreshRes = await axios.post(`${API}/auth/refresh`);
        const newToken = refreshRes.data.access_token;
        if (newToken) {
          updateToken(newToken);
          setUser(refreshRes.data.user);
          setLoading(false);
          return;
        }
      } catch {
        // Refresh also failed
      }

      // Not authenticated
      tokenRef.current = null;
      setToken(null);
      setUser(null);
      clearSession();
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    updateToken(access_token || 'cookie');
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register/client`, data);
    const { access_token, user: userData } = response.data;
    updateToken(access_token || 'cookie');
    setUser(userData);
    return userData;
  };

  const registerRestaurant = async (data) => {
    const response = await axios.post(`${API}/auth/register/restaurant`, data);
    const { access_token, user: userData } = response.data;
    updateToken(access_token || 'cookie');
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* best-effort */ }
    tokenRef.current = null;
    setToken(null);
    setUser(null);
    clearSession();
  };

  const setAuth = (accessToken, userData) => {
    updateToken(accessToken || 'cookie');
    setUser(userData);
  };

  const getAuthHeader = () => {
    const t = tokenRef.current;
    if (t && t !== 'cookie') return { Authorization: `Bearer ${t}` };
    return {};
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, register, registerRestaurant, logout, setAuth, getAuthHeader,
      isAuthenticated: !!token,
      isClient: user?.role === 'Client',
      isRestaurant: user?.role === 'Restaurant',
      isAdmin: user?.role === 'Admin',
    }}>
      {children}
    </AuthContext.Provider>
  );
};
